import { useState } from 'react';
import { Calendar, Repeat, Clock, Edit, Trash2, Plus, AlertTriangle, X } from 'lucide-react';
import { formatDateTime } from '../../utils/formatting';

export interface Schedule {
  scheduleId: string;
  siteId: string;
  userId: string;
  frequency: 'ONCE' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  nextRunAt: string;
  dayOfWeek?: number;
  dayOfMonth?: number;
  hour: number;
  minute: number;
  timezone: string;
  enabled: boolean;
  lastRunAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface ScheduleManagerProps {
  siteId: string;
  schedules: Schedule[];
  onCreateSchedule: () => void;
  onEditSchedule: (scheduleId: string) => void;
  onDeleteSchedule: (scheduleId: string) => void;
  onToggleSchedule: (scheduleId: string, enabled: boolean) => void;
  loading?: boolean;
}

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatTime(hour: number, minute: number): string {
  const date = new Date();
  date.setHours(hour, minute);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatScheduleDescription(schedule: Schedule): string {
  const time = formatTime(schedule.hour, schedule.minute);

  switch (schedule.frequency) {
    case 'ONCE': {
      const date = new Date(schedule.nextRunAt);
      const formattedDate = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return `One-time on ${formattedDate} at ${time}`;
    }
    case 'DAILY':
      return `Daily at ${time}`;
    case 'WEEKLY': {
      const dayName = schedule.dayOfWeek !== undefined ? DAYS_OF_WEEK[schedule.dayOfWeek] : 'Unknown';
      return `Weekly on ${dayName} at ${time}`;
    }
    case 'MONTHLY': {
      const day = schedule.dayOfMonth || 1;
      const suffix = getDaySuffix(day);
      return `Monthly on the ${day}${suffix} at ${time}`;
    }
    default:
      return 'Unknown schedule';
  }
}

function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

function ScheduleSkeleton() {
  return (
    <div className="card animate-pulse">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-gray-light rounded-full" />
        <div className="flex-1 space-y-3">
          <div className="h-5 bg-gray-light rounded w-3/4" />
          <div className="h-4 bg-gray-light rounded w-1/2" />
          <div className="h-4 bg-gray-light rounded w-1/3" />
        </div>
        <div className="flex gap-2">
          <div className="w-12 h-6 bg-gray-light rounded-full" />
          <div className="w-8 h-8 bg-gray-light rounded" />
          <div className="w-8 h-8 bg-gray-light rounded" />
        </div>
      </div>
    </div>
  );
}

interface DeleteConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  scheduleName: string;
}

function DeleteConfirmDialog({ isOpen, onClose, onConfirm, scheduleName }: DeleteConfirmDialogProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-primary/80"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md bg-white rounded-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-light">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-status-error/10 rounded-full flex items-center justify-center">
              <AlertTriangle className="text-status-error" size={20} />
            </div>
            <h2 className="text-heading-3 font-semibold">Delete Schedule</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-medium hover:text-dark-text rounded-card transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-body text-gray-medium mb-2">
            Are you sure you want to delete this schedule?
          </p>
          <p className="text-body-sm text-dark-text font-medium bg-gray-light/50 p-3 rounded-card">
            {scheduleName}
          </p>
          <p className="text-body-sm text-gray-medium mt-4">
            This action cannot be undone. The scheduled scan will no longer run.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-6 pt-0">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary flex-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="btn-primary flex-1 bg-status-error hover:bg-status-error/90"
          >
            Delete Schedule
          </button>
        </div>
      </div>
    </div>
  );
}

interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
}

function ToggleSwitch({ enabled, onChange, disabled }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
        ${enabled ? 'bg-coral' : 'bg-gray-light'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <span
        className={`
          inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
          ${enabled ? 'translate-x-6' : 'translate-x-1'}
        `}
      />
    </button>
  );
}

export function ScheduleManager({
  schedules,
  onCreateSchedule,
  onEditSchedule,
  onDeleteSchedule,
  onToggleSchedule,
  loading = false,
}: ScheduleManagerProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<Schedule | null>(null);

  const handleDeleteClick = (schedule: Schedule) => {
    setScheduleToDelete(schedule);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (scheduleToDelete) {
      onDeleteSchedule(scheduleToDelete.scheduleId);
      setScheduleToDelete(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setScheduleToDelete(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-heading-2 font-semibold">Scheduled Scans</h2>
        <button
          onClick={onCreateSchedule}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          Add Schedule
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="space-y-4">
          <ScheduleSkeleton />
          <ScheduleSkeleton />
        </div>
      )}

      {/* Empty State */}
      {!loading && schedules.length === 0 && (
        <div className="card text-center py-12">
          <Calendar size={48} className="mx-auto text-gray-medium mb-4" />
          <h3 className="text-heading-3 font-semibold mb-2">No Scheduled Scans</h3>
          <p className="text-body text-gray-medium mb-6">
            Set up automated scans to run on a regular schedule
          </p>
          <button
            onClick={onCreateSchedule}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={18} />
            Add Schedule
          </button>
        </div>
      )}

      {/* Schedule List */}
      {!loading && schedules.length > 0 && (
        <div className="space-y-4">
          {schedules.map((schedule) => {
            const description = formatScheduleDescription(schedule);
            const isRecurring = schedule.frequency !== 'ONCE';
            const FrequencyIcon = isRecurring ? Repeat : Calendar;

            return (
              <div
                key={schedule.scheduleId}
                className={`card-hover ${!schedule.enabled ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start gap-4">
                  {/* Frequency Icon */}
                  <div className={`
                    flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
                    ${schedule.enabled ? 'bg-coral/10' : 'bg-gray-light'}
                  `}>
                    <FrequencyIcon
                      size={20}
                      className={schedule.enabled ? 'text-coral' : 'text-gray-medium'}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="text-body font-medium mb-1">
                      {description}
                    </div>

                    {/* Next Run */}
                    <div className="flex items-center gap-1.5 text-body-sm text-gray-medium mb-1">
                      <Clock size={14} />
                      <span>Next run: {formatDateTime(schedule.nextRunAt)}</span>
                    </div>

                    {/* Last Run */}
                    {schedule.lastRunAt && (
                      <div className="text-caption text-gray-medium">
                        Last run: {formatDateTime(schedule.lastRunAt)}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0 flex items-center gap-3">
                    {/* Toggle */}
                    <ToggleSwitch
                      enabled={schedule.enabled}
                      onChange={(enabled) => onToggleSchedule(schedule.scheduleId, enabled)}
                    />

                    {/* Edit Button */}
                    <button
                      onClick={() => onEditSchedule(schedule.scheduleId)}
                      className="p-2 text-gray-medium hover:text-coral hover:bg-coral/10 rounded-card transition-colors"
                      title="Edit schedule"
                    >
                      <Edit size={18} />
                    </button>

                    {/* Delete Button */}
                    <button
                      onClick={() => handleDeleteClick(schedule)}
                      className="p-2 text-gray-medium hover:text-status-error hover:bg-status-error/10 rounded-card transition-colors"
                      title="Delete schedule"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        scheduleName={scheduleToDelete ? formatScheduleDescription(scheduleToDelete) : ''}
      />
    </div>
  );
}
